import { getAuthHeaders, extractCreatorId } from './authUtils';
import { getApiBaseUrlWithoutPath } from '../../config/environment';
import { adminUserService } from '../../services/adminUserService';

const CAT_CACHE_TTL_MS = 5 * 60 * 1000;
let categoriesCache = { list: null, at: 0 };

function teamProductOk(product, myId) {
  if (!myId) return true;
  return extractCreatorId(product.createdBy) === myId;
}

function teamOrderOk(order, myId) {
  if (!myId) return true;
  const items = order.items || [];
  return items.some((item) => {
    const product = item.product || item.productId;
    if (!product || typeof product !== 'object') return false;
    return extractCreatorId(product.createdBy) === myId;
  });
}

function matchesQuery(obj, q, fields) {
  const lower = q.toLowerCase();
  return fields.some((f) => {
    const v = obj[f];
    if (v == null) return false;
    return String(v).toLowerCase().includes(lower);
  });
}

async function fetchJson(url, signal) {
  const res = await fetch(url, { method: 'GET', headers: getAuthHeaders(), signal });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

/**
 * Cross-admin search: products, users, orders, categories; optional coupons.
 * Team members see only their products / related orders.
 */
export async function runAdminGlobalSearch(rawQuery, { signal, currentUser } = {}) {
  const q = rawQuery.trim();
  if (q.length < 2) {
    return { products: [], users: [], orders: [], categories: [], coupons: [] };
  }

  const myId = String(currentUser?._id || currentUser?.id || '');
  const isTeam = currentUser?.role === 'team';
  const apiUrl = getApiBaseUrlWithoutPath();
  const enc = encodeURIComponent(q);

  const productsUrl = `${apiUrl}/api/admin/products?limit=80&page=1&search=${enc}`;
  const ordersUrl = `${apiUrl}/api/admin/orders?limit=60&page=1&search=${enc}`;

  const [productsJ, usersRes, ordersJ, couponsJ] = await Promise.allSettled([
    fetchJson(productsUrl, signal),
    adminUserService.getUsers({ search: q, limit: 10, page: 1 }),
    fetchJson(ordersUrl, signal),
    fetchJson(`${apiUrl}/api/admin/coupons?limit=8&search=${enc}`, signal),
  ]);

  let products = [];
  if (productsJ.status === 'fulfilled' && productsJ.value?.success && Array.isArray(productsJ.value.data)) {
    products = productsJ.value.data;
  }
  if (isTeam && myId) {
    products = products.filter((p) => teamProductOk(p, myId));
  }
  const qLower = q.toLowerCase();
  products = products.filter(
    (p) =>
      matchesQuery(p, q, ['name', 'sku', 'description', 'brand']) ||
      String(p._id || p.id || '')
        .toLowerCase()
        .includes(qLower)
  );
  products = products.slice(0, 12);

  let users = [];
  if (usersRes.status === 'fulfilled' && usersRes.value?.success) {
    const arr = usersRes.value.data || usersRes.value.users || [];
    users = Array.isArray(arr) ? arr.slice(0, 10) : [];
  }

  let orders = [];
  if (ordersJ.status === 'fulfilled' && ordersJ.value) {
    const ok = ordersJ.value.success !== false;
    const arr = ordersJ.value.data || ordersJ.value.orders || [];
    if (ok && Array.isArray(arr)) orders = arr;
  }
  if (isTeam && myId) {
    orders = orders.filter((o) => teamOrderOk(o, myId));
  }
  orders = orders.filter(
    (o) =>
      matchesQuery(o, q, ['orderNumber', 'status']) ||
      (o.customer && matchesQuery(o.customer, q, ['name', 'email', 'phone'])) ||
      String(o._id || o.id || '')
        .toLowerCase()
        .includes(qLower)
  );
  orders = orders.slice(0, 10);

  let categories = [];
  const now = Date.now();
  try {
    if (!categoriesCache.list || now - categoriesCache.at > CAT_CACHE_TTL_MS) {
      const cj = await fetchJson(`${apiUrl}/api/admin/categories`, signal);
      let list = [];
      if (cj?.success && Array.isArray(cj.data)) list = cj.data;
      else if (Array.isArray(cj)) list = cj;
      categoriesCache = { list, at: now };
    }
    const all = categoriesCache.list || [];
    categories = all
      .filter((c) =>
        matchesQuery(c, q, ['name', 'slug', 'description'])
      )
      .slice(0, 8);
  } catch {
    categories = [];
  }

  let coupons = [];
  if (couponsJ.status === 'fulfilled' && couponsJ.value?.success && Array.isArray(couponsJ.value.data)) {
    coupons = couponsJ.value.data.slice(0, 6);
  }

  return { products, users, orders, categories, coupons };
}
